import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Mic, Calendar, TrendingUp, Heart, Search, Brain, Target, ArrowLeft, Sparkles, CheckCircle, Zap, BarChart3, PieChart, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const Journal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  // Use relative path for API calls - backend is on same domain
  const API_BASE_URL = '';
  
  const [activeView, setActiveView] = useState('freeform');
  const [journalText, setJournalText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Filter states
  const [filterMood, setFilterMood] = useState(null);
  const [filterEntryType, setFilterEntryType] = useState(null);
  const [filterCategories, setFilterCategories] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
  const [timeWindow, setTimeWindow] = useState('7days'); // For AI Insights

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setJournalText(prev => prev + ' ' + transcript);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognitionInstance.onend = () => {
        setIsListening(false);
      };
      
      setRecognition(recognitionInstance);
    }
  }, []);

  // Load journal entries
  useEffect(() => {
    fetchEntries();
    fetchInsights();
  }, []);
  
  // Refetch insights when time window changes
  useEffect(() => {
    if (activeView === 'insights') {
      fetchInsights();
    }
  }, [timeWindow]);

  const fetchInsights = async () => {
    try {
      setInsightsLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/journal/insights?time_window=${timeWindow}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      } else {
        console.error('Failed to fetch insights:', response.status);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        setError('Please log in to view your journal');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/journal/entries`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend returns array directly, not wrapped in object
        setEntries(Array.isArray(data) ? data : []);
        setError(null);
      } else if (response.status === 404) {
        // 404 means no entries yet, which is a valid state
        setEntries([]);
        setError(null);
      } else {
        // Only show error for actual server/network errors
        console.error('Failed to fetch entries:', response.status);
        setError('Failed to load entries. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      // Only show error for actual connection issues
      setError('Connection error. Please check your internet.');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceInput = () => {
    if (!recognition) {
      toast({
        title: "Not Supported",
        description: "Speech recognition is not supported in your browser",
        variant: "destructive"
      });
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
    }
  };

  const handleSaveEntry = async () => {
    if (!journalText.trim()) {
      toast({
        title: "Entry Required",
        description: "Please write something before saving",
        variant: "destructive"
      });
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please log in again",
          variant: "destructive"
        });
        return;
      }

      const isEditing = isEditMode && editingEntry;
      const url = isEditing 
        ? `${API_BASE_URL}/api/journal/entries/${editingEntry.id}`
        : `${API_BASE_URL}/api/journal/entries`;
      const method = isEditing ? 'PUT' : 'POST';

      console.log(isEditing ? 'Updating journal entry...' : 'Saving journal entry...');
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: journalText,
          mood: selectedMood,
          prompt_id: selectedPrompt,
          tags: []
        })
      });

      console.log('Save response:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Entry saved:', data);
        toast({
          title: isEditing ? "✨ Entry Updated!" : "✨ Entry Saved!",
          description: isEditing ? "Your journal entry has been updated successfully" : "Your journal entry has been saved successfully",
          duration: 3000
        });
        setJournalText('');
        setSelectedMood(null);
        setSelectedPrompt(null);
        setIsEditMode(false);
        setEditingEntry(null);
        fetchEntries();
        fetchInsights();
      } else {
        const errorText = await response.text();
        console.error('Save failed:', response.status, errorText);
        toast({
          title: "Save Failed",
          description: `Failed to save entry: ${response.status}`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      toast({
        title: "Error",
        description: "Error saving entry. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDiscardEntry = () => {
    setJournalText('');
    setSelectedMood(null);
    setSelectedPrompt(null);
    setIsEditMode(false);
    setEditingEntry(null);
    toast({
      title: "Discarded",
      description: "Changes have been discarded",
      duration: 2000
    });
  };

  // Mock function to add categories and entry_type to entries (will be replaced by backend)
  const enrichEntry = (entry) => {
    // Mock categories based on content keywords (will be AI-generated on backend)
    const mockCategories = [];
    const content = entry.content?.toLowerCase() || '';
    
    if (content.includes('gratitude') || content.includes('grateful') || content.includes('thankful') || content.includes('blessed')) {
      mockCategories.push('gratitude');
    }
    if (content.includes('heal') || content.includes('process') || content.includes('aware') || content.includes('understand')) {
      mockCategories.push('healing');
    }
    if (content.includes('love myself') || content.includes('self-love') || content.includes('worth') || content.includes('deserve')) {
      mockCategories.push('self_love');
    }
    if (content.includes('grow') || content.includes('learn') || content.includes('align') || content.includes('transform')) {
      mockCategories.push('growth');
    }
    if (content.includes('relationship') || content.includes('connect') || content.includes('friend') || content.includes('family')) {
      mockCategories.push('relationships');
    }
    if (content.includes('pray') || content.includes('faith') || content.includes('spiritual') || content.includes('divine')) {
      mockCategories.push('spirituality');
    }

    // If no categories matched, assign random ones (max 2)
    if (mockCategories.length === 0) {
      const randomCats = ['healing', 'self_love', 'growth', 'gratitude'];
      mockCategories.push(randomCats[Math.floor(Math.random() * randomCats.length)]);
    }
    if (mockCategories.length === 1) {
      const remainingCats = ['healing', 'self_love', 'growth', 'gratitude', 'relationships', 'spirituality']
        .filter(c => !mockCategories.includes(c));
      if (remainingCats.length > 0) {
        mockCategories.push(remainingCats[Math.floor(Math.random() * remainingCats.length)]);
      }
    }

    return {
      ...entry,
      categories: mockCategories.slice(0, 2),
      entry_type: entry.prompt_id ? 'guided_prompt' : 'brain_dump'
    };
  };

  // Filter and sort entries
  const getFilteredAndSortedEntries = () => {
    let filtered = entries.map(enrichEntry);

    // Apply filters
    if (filterMood) {
      filtered = filtered.filter(e => e.mood === parseInt(filterMood));
    }
    if (filterEntryType) {
      filtered = filtered.filter(e => e.entry_type === filterEntryType);
    }
    if (filterCategories.length > 0) {
      filtered = filtered.filter(e => 
        e.categories.some(cat => filterCategories.includes(cat))
      );
    }

    // Apply sorting
    if (sortBy === 'recent') {
      filtered = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      filtered = filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'mood_high') {
      filtered = filtered.sort((a, b) => (b.mood || 0) - (a.mood || 0));
    } else if (sortBy === 'mood_low') {
      filtered = filtered.sort((a, b) => (a.mood || 0) - (b.mood || 0));
    }

    return filtered;
  };

  const views = [
    { id: 'freeform', name: 'Journal', icon: BookOpen },
    { id: 'guided', name: 'Guided Prompts', icon: Target },
    { id: 'insights', name: 'AI Insights', icon: Brain }
  ];

  const guidedPrompts = [
    {
      category: 'Self-Discovery',
      prompts: [
        "What does self-love mean to me today?",
        "What am I most proud of about myself right now?",
        "What boundary do I need to set for my peace?"
      ]
    },
    {
      category: 'Manifestation',
      prompts: [
        "What am I calling into my life?",
        "How do I want to feel in my dream reality?",
        "What aligned action can I take today toward my desires?"
      ]
    },
    {
      category: 'Gratitude',
      prompts: [
        "What am I grateful for in this moment?",
        "What small win can I celebrate today?",
        "Who has shown me love recently, and how?"
      ]
    },
    {
      category: 'Shadow Work',
      prompts: [
        "What fear is holding me back right now?",
        "What pattern am I ready to release?",
        "What would I say to my younger self?"
      ]
    }
  ];

  const moods = [
    { emoji: '😔', label: 'Sad', value: 1 },
    { emoji: '😐', label: 'Okay', value: 2 },
    { emoji: '🙂', label: 'Good', value: 3 },
    { emoji: '😊', label: 'Great', value: 4 },
    { emoji: '🤩', label: 'Empowered', value: 5 }
  ];

  const categories = [
    { id: 'healing', label: 'Healing & Awareness', icon: '🌙' },
    { id: 'self_love', label: 'Self-Love & Worth', icon: '💕' },
    { id: 'growth', label: 'Growth & Alignment', icon: '🌱' },
    { id: 'gratitude', label: 'Gratitude & Joy', icon: '🌸' },
    { id: 'relationships', label: 'Relationships & Connection', icon: '💫' },
    { id: 'spirituality', label: 'Spirituality & Faith', icon: '✨' }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center">
        <Card className="p-8 text-center">
          <div className="text-6xl mb-4">📝</div>
          <p className="text-gray-600 font-sans">Loading your journal...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 pb-20" style={{paddingTop: '64px'}}>
      <div className="max-w-6xl mx-auto px-4">
        
        {/* Error Message */}
        {error && (
          <Card className="p-4 mb-4 bg-red-50 border-red-200">
            <p className="text-red-700 text-center font-sans">{error}</p>
            <Button 
              onClick={fetchEntries} 
              className="mt-2 w-full bg-red-500 hover:bg-red-600 text-white"
            >
              Retry
            </Button>
          </Card>
        )}

        {/* Header */}
        <div className="flex items-center space-x-2 mb-6">
          <button
            onClick={() => navigate('/app')}
            className="p-1.5 hover:bg-white/80 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-serif font-bold text-gray-900">Journaling</h1>
            <p className="text-gray-600 text-xs">Your sacred space for reflection & growth</p>
          </div>
        </div>

        {/* View Tabs - Compact Mobile Design */}
        <div className="mb-8 bg-white/80 p-1.5 rounded-2xl border border-rose-200">
          <div className="grid grid-cols-3 gap-1">
            {views.map((view) => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  onClick={() => {
                    setActiveView(view.id);
                    if (view.id === 'insights') {
                      fetchInsights();
                    }
                  }}
                  className={`flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all ${
                    activeView === view.id
                      ? 'bg-rose-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-rose-50'
                  }`}
                >
                  <Icon className="w-4 h-4 mb-1" />
                  <span className="font-medium text-xs leading-tight text-center">{view.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Brain Dump View */}
        {activeView === 'freeform' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sidebar - Desktop Only */}
            <div className="hidden lg:block lg:col-span-1">
              {/* All Entries List */}
              <Card className="p-4 bg-white/80 border-rose-200 mt-6">
                <h3 className="font-serif font-semibold text-gray-900 mb-2">Your Reflections - {entries.length} {entries.length === 1 ? 'Entry' : 'Entries'} So Far 🌸</h3>
                <p className="text-xs text-gray-600 mb-4 italic">Each entry is a piece of your growth. Keep showing up for yourself 💕</p>
                
                {/* Filter Bar */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Mood Filter */}
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Mood</label>
                      <select 
                        value={filterMood || ''} 
                        onChange={(e) => setFilterMood(e.target.value || null)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-rose-300"
                      >
                        <option value="">All</option>
                        {moods.map(mood => (
                          <option key={mood.value} value={mood.value}>{mood.emoji} {mood.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Entry Type Filter */}
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Entry Type</label>
                      <select 
                        value={filterEntryType || ''} 
                        onChange={(e) => setFilterEntryType(e.target.value || null)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-rose-300"
                      >
                        <option value="">All</option>
                        <option value="brain_dump">Brain Dump</option>
                        <option value="guided_prompt">Guided</option>
                      </select>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Sort By</label>
                      <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-rose-300"
                      >
                        <option value="recent">Recent</option>
                        <option value="oldest">Oldest</option>
                        <option value="mood_high">High Mood</option>
                        <option value="mood_low">Low Mood</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Clear Filters - Full Width */}
                  <div>
                    <button
                      onClick={() => {
                        setFilterMood(null);
                        setFilterEntryType(null);
                        setFilterCategories([]);
                        setSortBy('recent');
                      }}
                      className="w-full text-sm px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors font-medium"
                    >
                      Clear All Filters
                    </button>
                  </div>

                  {/* Categories Filter */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Categories (multi-select)</label>
                    <div className="flex flex-col gap-2">
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            if (filterCategories.includes(cat.id)) {
                              setFilterCategories(filterCategories.filter(c => c !== cat.id));
                            } else {
                              setFilterCategories([...filterCategories, cat.id]);
                            }
                          }}
                          className={`text-xs px-3 py-1.5 rounded-full border-2 transition-all whitespace-nowrap text-left ${
                            filterCategories.includes(cat.id)
                              ? 'border-rose-500 bg-rose-50 text-rose-700 font-medium'
                              : 'border-gray-300 bg-white text-gray-600 hover:border-rose-300'
                          }`}
                        >
                          {cat.icon} {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {entries.length === 0 ? (
                    <p className="text-gray-500 text-center py-4 text-sm">No entries yet</p>
                  ) : (
                    getFilteredAndSortedEntries().map((entry) => (
                      <div key={entry.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-white transition-all group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <span className="text-lg">{moods.find(m => m.value === entry.mood)?.emoji || '🙂'}</span>
                              <span className="text-xs font-medium text-gray-700">
                                {moods.find(m => m.value === entry.mood)?.label || 'Good'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} {new Date(entry.created_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              {entry.entry_type === 'guided_prompt' ? '📝 Guided' : '🧠 Brain Dump'}
                            </span>
                          </div>
                          <div className="flex space-x-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setJournalText(entry.content);
                                setSelectedMood(entry.mood);
                                setIsEditMode(true);
                                setEditingEntry(entry);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              className="p-1 hover:bg-blue-100 rounded text-blue-600"
                              title="Edit"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this entry?')) {
                                  try {
                                    const response = await fetch(`${API_BASE_URL}/api/journal/entries/${entry.id}`, {
                                      method: 'DELETE',
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('auth_token')}` }
                                    });
                                    if (response.ok) {
                                      fetchEntries();
                                    }
                                  } catch (error) {
                                    console.error('Delete failed:', error);
                                  }
                                }
                              }}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                              title="Delete"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {/* Entry title (prompt for guided entries) */}
                        {entry.prompt_id && (
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">{entry.prompt_id}</h4>
                        )}
                        <p className="text-gray-700 text-sm line-clamp-2 mb-2">{entry.content}</p>
                        {/* Category badges */}
                        {entry.categories && entry.categories.length > 0 && (
                          <div className="flex gap-1.5 flex-wrap">
                            {entry.categories.map((catId) => {
                              const cat = categories.find(c => c.id === catId);
                              return cat ? (
                                <span key={catId} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-200">
                                  {cat.icon} {cat.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>

            {/* Main Content - Writing Area */}
            <div className="lg:col-span-2">
              {/* Writing Interface */}
              <Card className={`p-6 bg-white/80 mb-6 ${isEditMode ? 'border-blue-400 border-2' : 'border-rose-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif font-semibold text-gray-900">
                    {isEditMode ? '✏️ Editing Entry' : selectedPrompt || 'Brain Dump - Free Writing'}
                  </h3>
                  {isEditMode && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                      Edit Mode
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-4 italic">
                  {isEditMode ? 'Make your changes below, then save or discard' : "Your journal is waiting… this is your space to release, reflect, and write from the heart. 💕"}
                </p>
                
                {/* Text Area */}
                <div className="relative">
                  <textarea
                    value={journalText}
                    onChange={(e) => setJournalText(e.target.value)}
                    placeholder="Start writing what's on your heart, or tap the mic to speak it aloud. 💕"
                    className="w-full h-64 p-4 pb-12 border border-rose-200 rounded-lg resize-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(transparent, transparent 24px, #f1f5f9 24px, #f1f5f9 25px)',
                      lineHeight: '25px'
                    }}
                  />
                  {/* Voice Input Button - Bottom Right */}
                  <button 
                    onClick={handleVoiceInput}
                    className={`absolute bottom-3 right-3 p-3 rounded-full transition-all shadow-md ${
                      isListening ? 'bg-rose-500 text-white animate-pulse' : 'bg-white hover:bg-rose-100 text-rose-500 border-2 border-rose-200'
                    }`}
                    title={isListening ? 'Listening...' : 'Voice Input'}
                  >
                    <Mic className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Mood Selector */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="text-sm font-medium text-gray-700 mb-3 block text-center">How are you feeling right now?</label>
                  <div className="flex justify-between items-start px-2 mb-3">
                    {moods.map((mood) => (
                      <button
                        key={mood.value}
                        onClick={() => setSelectedMood(mood.value)}
                        className={`relative flex flex-col items-center transition-all duration-300 transform ${
                          selectedMood === mood.value 
                            ? 'scale-110' 
                            : 'hover:scale-105'
                        }`}
                        title={mood.label}
                      >
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                          selectedMood === mood.value 
                            ? 'bg-rose-500 shadow-lg ring-4 ring-rose-200' 
                            : 'bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-rose-300'
                        }`}>
                          <span className="text-2xl">{mood.emoji}</span>
                        </div>
                        <span className={`text-xs mt-2 font-medium transition-colors ${
                          selectedMood === mood.value 
                            ? 'text-rose-600' 
                            : 'text-gray-600'
                        }`}>
                          {mood.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save and Discard Buttons */}
                <div className="flex justify-end items-center gap-3 mt-6">
                  {(isEditMode || journalText.trim()) && (
                    <Button 
                      onClick={handleDiscardEntry}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full"
                    >
                      ✕ {isEditMode ? 'Discard Changes' : 'Clear'}
                    </Button>
                  )}
                  <Button 
                    onClick={handleSaveEntry}
                    className="bg-rose-500 hover:bg-rose-600 text-white rounded-full"
                  >
                    {isEditMode ? '✓ Update Entry' : '📝 Save Entry'}
                  </Button>
                </div>
              </Card>

              {/* All Entries - Mobile Only */}
              <Card className="p-4 bg-white/80 border-rose-200 lg:hidden">
                <h3 className="font-serif font-semibold text-gray-900 mb-2">Your Reflections - {entries.length} {entries.length === 1 ? 'Entry' : 'Entries'} So Far 🌸</h3>
                <p className="text-xs text-gray-600 mb-4 italic">Each entry is a piece of your growth. Keep showing up for yourself 💕</p>
                
                {/* Filter Bar - Mobile */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Mood Filter */}
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Mood</label>
                      <select 
                        value={filterMood || ''} 
                        onChange={(e) => setFilterMood(e.target.value || null)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-rose-300"
                      >
                        <option value="">All</option>
                        {moods.map(mood => (
                          <option key={mood.value} value={mood.value}>{mood.emoji} {mood.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Entry Type Filter */}
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Type</label>
                      <select 
                        value={filterEntryType || ''} 
                        onChange={(e) => setFilterEntryType(e.target.value || null)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-rose-300"
                      >
                        <option value="">All</option>
                        <option value="brain_dump">Brain Dump</option>
                        <option value="guided_prompt">Guided</option>
                      </select>
                    </div>

                    {/* Sort */}
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Sort</label>
                      <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-rose-300"
                      >
                        <option value="recent">Recent</option>
                        <option value="oldest">Oldest</option>
                        <option value="mood_high">High Mood</option>
                        <option value="mood_low">Low Mood</option>
                      </select>
                    </div>

                    {/* Clear Filters */}
                    <div className="flex items-end">
                      <button
                        onClick={() => {
                          setFilterMood(null);
                          setFilterEntryType(null);
                          setFilterCategories([]);
                          setSortBy('recent');
                        }}
                        className="w-full text-sm px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Categories Filter - Mobile */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Categories</label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {categories.map(cat => {
                        // Create very short mobile labels
                        const mobileLabels = {
                          'healing': 'Healing',
                          'self_love': 'Love',
                          'growth': 'Growth',
                          'gratitude': 'Gratitude',
                          'relationships': 'Relate',
                          'spirituality': 'Spirit'
                        };
                        return (
                          <button
                            key={cat.id}
                            onClick={() => {
                              if (filterCategories.includes(cat.id)) {
                                setFilterCategories(filterCategories.filter(c => c !== cat.id));
                              } else {
                                setFilterCategories([...filterCategories, cat.id]);
                              }
                            }}
                            title={cat.label}
                            className={`text-[10px] px-1.5 py-1 rounded-md border-2 transition-all flex flex-col items-center gap-0.5 ${
                              filterCategories.includes(cat.id)
                                ? 'border-rose-500 bg-rose-50 text-rose-700 font-medium'
                                : 'border-gray-300 bg-white text-gray-600'
                            }`}
                          >
                            <span className="text-sm">{cat.icon}</span>
                            <span className="text-[9px] leading-tight">{mobileLabels[cat.id]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {entries.length === 0 ? (
                    <p className="text-gray-500 text-center py-4 text-sm">No entries yet</p>
                  ) : (
                    getFilteredAndSortedEntries().map((entry) => (
                      <div key={entry.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-white transition-all group">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <span className="text-base">{moods.find(m => m.value === entry.mood)?.emoji || '🙂'}</span>
                              <span className="text-xs font-medium text-gray-700">
                                {moods.find(m => m.value === entry.mood)?.label || 'Good'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} {new Date(entry.created_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                              {entry.entry_type === 'guided_prompt' ? '📝' : '🧠'}
                            </span>
                          </div>
                          <div className="flex space-x-1 flex-shrink-0">
                            <button
                              onClick={() => {
                                setJournalText(entry.content);
                                setSelectedMood(entry.mood);
                                setIsEditMode(true);
                                setEditingEntry(entry);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              className="p-1 hover:bg-blue-100 rounded text-blue-600"
                              title="Edit"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this entry?')) {
                                  try {
                                    const response = await fetch(`${API_BASE_URL}/api/journal/entries/${entry.id}`, {
                                      method: 'DELETE',
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('auth_token')}` }
                                    });
                                    if (response.ok) {
                                      fetchEntries();
                                      fetchInsights();
                                    }
                                  } catch (error) {
                                    console.error('Delete failed:', error);
                                  }
                                }
                              }}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                              title="Delete"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        {/* Entry title (prompt for guided entries) - Mobile */}
                        {entry.prompt_id && (
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">{entry.prompt_id}</h4>
                        )}
                        <p className="text-gray-700 text-sm line-clamp-2 mb-2">{entry.content}</p>
                        {/* Category badges - Mobile */}
                        {entry.categories && entry.categories.length > 0 && (
                          <div className="flex gap-1.5 flex-wrap">
                            {entry.categories.map((catId) => {
                              const cat = categories.find(c => c.id === catId);
                              return cat ? (
                                <span key={catId} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-200">
                                  {cat.icon} {cat.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Guided Prompts View */}
        {activeView === 'guided' && (
          <div className="max-w-4xl mx-auto">
            <Card className="p-6 bg-white/80 border-rose-200 mb-6">
              <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Guided Reflection Prompts</h3>
              <p className="text-gray-600 mb-6">Choose a prompt to deepen your self-discovery journey 🌸</p>

              <div className="space-y-6">
                {guidedPrompts.map((category, catIndex) => (
                  <div key={catIndex}>
                    <h4 className="text-lg font-serif font-semibold text-rose-600 mb-3">{category.category}</h4>
                    <div className="space-y-2">
                      {category.prompts.map((prompt, promptIndex) => (
                        <button
                          key={promptIndex}
                          onClick={() => {
                            setSelectedPrompt(prompt);
                            setActiveView('freeform');
                            setJournalText('');
                          }}
                          className="w-full text-left p-4 bg-gradient-to-r from-white to-rose-50 rounded-lg border border-rose-200 hover:border-rose-400 hover:shadow-md transition-all"
                        >
                          <p className="text-gray-800">{prompt}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* AI Insights View */}
        {activeView === 'insights' && (
          <div>
            {/* Header with Time Window Filter */}
            <Card className="p-6 bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-serif font-bold text-2xl text-gray-900 mb-2">Your Journal Insights 💫</h3>
                  <p className="text-sm text-gray-600 italic">
                    Gentle guidance based on your reflections - awareness that turns into growth 🌸
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTimeWindow('7days')}
                    className={`px-3 py-1.5 text-xs md:text-sm rounded-full transition-all whitespace-nowrap ${
                      timeWindow === '7days'
                        ? 'bg-rose-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => setTimeWindow('30days')}
                    className={`px-3 py-1.5 text-xs md:text-sm rounded-full transition-all whitespace-nowrap ${
                      timeWindow === '30days'
                        ? 'bg-rose-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => setTimeWindow('all')}
                    className={`px-3 py-1.5 text-xs md:text-sm rounded-full transition-all whitespace-nowrap ${
                      timeWindow === 'all'
                        ? 'bg-rose-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    All Time
                  </button>
                </div>
              </div>
            </Card>

            {insightsLoading ? (
              <Card className="p-8 text-center">
                <div className="text-4xl mb-4">🧠</div>
                <p className="text-gray-600">Loading your insights...</p>
              </Card>
            ) : entries.length === 0 ? (
              <Card className="p-12 text-center bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200">
                <div className="text-6xl mb-4">💕</div>
                <h4 className="font-serif font-bold text-xl text-gray-900 mb-2">Start your first reflection to unlock insights 💕</h4>
                <p className="text-gray-600 text-sm">Your journey begins with a single entry. Start writing to discover patterns and growth.</p>
              </Card>
            ) : (
              <>
                {/* Three AI-Generated Insight Blocks */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Emotional Reflection */}
                  <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <div className="text-lg mb-2">🌼</div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Emotional Reflection</h4>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {(() => {
                        const avgMood = insights?.average_mood || 0;
                        if (avgMood >= 4.5) return "You're radiating positivity and inner alignment - your heart is open and thriving.";
                        if (avgMood >= 3.5) return "You're navigating life with steady grace, showing resilience even in challenging moments.";
                        if (avgMood >= 2.5) return "You're in a season of processing and growth - every difficult emotion is teaching you something profound.";
                        return "You're moving through tender times with courage - remember, healing isn't linear and you're exactly where you need to be.";
                      })()}
                    </p>
                  </Card>

                  {/* This Week's Theme */}
                  <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                    <div className="text-lg mb-2">💫</div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">This Week's Theme</h4>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      {(() => {
                        const allEntries = entries.map(enrichEntry);
                        const categoryCounts = {};
                        allEntries.forEach(e => {
                          e.categories.forEach(cat => {
                            categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                          });
                        });
                        const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
                        if (!topCategory) return "Your reflections are just beginning - patterns will emerge as you continue writing.";
                        
                        const cat = categories.find(c => c.id === topCategory[0]);
                        const messages = {
                          'healing': "Your focus on healing shows deep self-awareness - you're giving yourself permission to process and grow.",
                          'self_love': "You're prioritizing self-love, which is the foundation of all transformation - this is powerful work.",
                          'growth': "Your commitment to growth is evident - you're actively choosing evolution over comfort.",
                          'gratitude': "Gratitude is shaping your perspective - this practice is opening doors to abundance and joy.",
                          'relationships': "You're exploring connection deeply - relationships are mirrors for your own growth journey.",
                          'spirituality': "Your spiritual exploration is grounding you - trust the divine timing of your path."
                        };
                        return `${cat?.icon} ${cat?.label.split(' ')[0]}: ${messages[topCategory[0]] || "You're exploring what matters most to your heart."}`;
                      })()}
                    </p>
                  </Card>

                  {/* Reflect On This */}
                  <Card className="p-4 bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
                    <div className="text-lg mb-2">✍️</div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Reflect On This</h4>
                    <p className="text-xs text-gray-700 leading-relaxed mb-3">
                      {(() => {
                        const allEntries = entries.map(enrichEntry);
                        const categoryCounts = {};
                        allEntries.forEach(e => {
                          e.categories.forEach(cat => {
                            categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                          });
                        });
                        const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
                        
                        const prompts = {
                          'healing': 'What part of my healing journey am I most proud of right now?',
                          'self_love': 'When do I feel most connected to my authentic self?',
                          'growth': 'What belief about myself is ready to evolve?',
                          'gratitude': 'What unexpected blessing showed up in my life this week?',
                          'relationships': 'How am I showing up differently in my relationships lately?',
                          'spirituality': 'What does my intuition want me to know today?'
                        };
                        return prompts[topCategory?.[0]] || "What am I learning about myself through this journaling practice?";
                      })()}
                    </p>
                    <button
                      onClick={() => {
                        const allEntries = entries.map(enrichEntry);
                        const categoryCounts = {};
                        allEntries.forEach(e => {
                          e.categories.forEach(cat => {
                            categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                          });
                        });
                        const topCategory = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])[0];
                        
                        const prompts = {
                          'healing': 'What part of my healing journey am I most proud of right now?',
                          'self_love': 'When do I feel most connected to my authentic self?',
                          'growth': 'What belief about myself is ready to evolve?',
                          'gratitude': 'What unexpected blessing showed up in my life this week?',
                          'relationships': 'How am I showing up differently in my relationships lately?',
                          'spirituality': 'What does my intuition want me to know today?'
                        };
                        const prompt = prompts[topCategory?.[0]] || "What am I learning about myself through this journaling practice?";
                        setSelectedPrompt(prompt);
                        setActiveView('freeform');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-xs px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-full font-medium transition-colors"
                    >
                      Journal This Now →
                    </button>
                  </Card>
                </div>

                {/* Totals & Consistency */}
                <div className="mb-6">
                  <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-rose-500" />
                    Totals & Consistency
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 text-center">
                      <div className="text-2xl font-bold text-blue-600">{insights?.total_entries || 0}</div>
                      <div className="text-xs text-gray-700 mt-1">Total Entries</div>
                      <div className="text-xs text-gray-500 mt-1">({timeWindow === '7days' ? 'Last 7 days' : timeWindow === '30days' ? 'Last 30 days' : 'All time'})</div>
                    </Card>
                    <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 text-center">
                      <div className="text-2xl font-bold text-purple-600">{insights?.entries_this_week || 0}</div>
                      <div className="text-xs text-gray-700 mt-1">Active Days</div>
                      <div className="text-xs text-gray-500 mt-1">Days with entries</div>
                    </Card>
                    <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 text-center">
                      <div className="text-2xl font-bold text-amber-600">{insights?.current_streak || 0} 🔥</div>
                      <div className="text-xs text-gray-700 mt-1">Streak Days</div>
                      <div className="text-xs text-gray-500 mt-1">Consecutive days</div>
                    </Card>
                    <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200 text-center">
                      <div className="text-2xl font-bold text-green-600">{insights?.total_entries || 0}</div>
                      <div className="text-xs text-gray-700 mt-1">All Time Total</div>
                      <div className="text-xs text-gray-500 mt-1">Every entry counts</div>
                    </Card>
                  </div>
                </div>

                {/* Mood Analytics */}
                <div className="mb-6">
                  <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-rose-500" />
                    Mood Analytics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4 bg-white/80 border-rose-200">
                      <div className="text-sm font-medium text-gray-700 mb-2">Average Mood Score</div>
                      <div className="flex items-end gap-2">
                        <div className="text-3xl font-bold text-rose-600">
                          {insights?.average_mood ? insights.average_mood.toFixed(1) : '-'}
                        </div>
                        <div className="text-sm text-gray-500 mb-1">/ 5.0</div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600">
                        {insights?.average_mood >= 4 ? '🤩 Empowered & thriving' : 
                         insights?.average_mood >= 3 ? '🙂 Balanced & steady' : 
                         insights?.average_mood >= 2 ? '😐 Processing & growing' :
                         insights?.average_mood ? '😔 Moving through difficulty' : 
                         'Start journaling to track'}
                      </div>
                    </Card>
                    
                    <Card className="p-4 bg-white/80 border-rose-200">
                      <div className="text-sm font-medium text-gray-700 mb-3">Mood Distribution</div>
                      <div className="space-y-1.5">
                        {moods.map(mood => {
                          const count = entries.filter(e => e.mood === mood.value).length;
                          const percentage = entries.length > 0 ? Math.round((count / entries.length) * 100) : 0;
                          return (
                            <div key={mood.value} className="flex items-center gap-2">
                              <span className="text-sm">{mood.emoji}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-rose-400 to-pink-500 h-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 w-10 text-right">{percentage}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </Card>

                    <Card className="p-4 bg-white/80 border-rose-200">
                      <div className="text-sm font-medium text-gray-700 mb-2">Mood Trend</div>
                      <div className="flex items-center gap-2 mt-4">
                        <TrendingUp className="w-8 h-8 text-green-500" />
                        <div className="text-xs text-gray-600">
                          <div className="font-medium">Trending Positive</div>
                          <div className="text-gray-500">+0.3 vs previous period</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        Your emotional trajectory is moving upward. Keep nurturing what serves you 💕
                      </p>
                    </Card>
                  </div>
                </div>

                {/* Category Insights */}
                <div className="mb-6">
                  <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                    <PieChart className="w-4 h-4 mr-2 text-rose-500" />
                    Category Insights
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-4 bg-white/80 border-rose-200">
                      <div className="text-sm font-medium text-gray-700 mb-3">Top Categories</div>
                      <div className="space-y-2">
                        {categories.slice(0, 3).map((cat, index) => {
                          const allEntries = entries.map(enrichEntry);
                          const count = allEntries.filter(e => e.categories.includes(cat.id)).length;
                          return (
                            <div key={cat.id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{cat.icon}</span>
                                <span className="text-sm font-medium text-gray-800">{cat.label}</span>
                              </div>
                              <span className="text-sm text-purple-600 font-bold">{count} entries</span>
                            </div>
                          );
                        })}
                      </div>
                    </Card>

                    <Card className="p-4 bg-white/80 border-rose-200">
                      <div className="text-sm font-medium text-gray-700 mb-3">Category Mix</div>
                      <div className="space-y-1.5">
                        {categories.map(cat => {
                          const allEntries = entries.map(enrichEntry);
                          const count = allEntries.filter(e => e.categories.includes(cat.id)).length;
                          const percentage = allEntries.length > 0 ? Math.round((count / allEntries.length) * 100) : 0;
                          return (
                            <div key={cat.id} className="flex items-center gap-2">
                              <div className="flex items-center gap-1 w-40 flex-shrink-0">
                                <span className="text-sm">{cat.icon}</span>
                                <span className="text-xs text-gray-700 truncate">{cat.label}</span>
                              </div>
                              <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-purple-400 to-pink-500 h-full transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 w-10 text-right">{percentage}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Entry Type Mix */}
                <div className="mb-6">
                  <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                    <BarChart3 className="w-4 h-4 mr-2 text-rose-500" />
                    Entry Type Mix
                  </h4>
                  <Card className="p-6 bg-white/80 border-rose-200">
                    <div className="grid grid-cols-2 gap-8">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-blue-600">
                          {Math.round((entries.filter(e => !e.prompt_id).length / Math.max(entries.length, 1)) * 100)}%
                        </div>
                        <div className="text-sm text-gray-700 mt-2">🧠 Brain Dump Entries</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {entries.filter(e => !e.prompt_id).length} of {entries.length} entries
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-4xl font-bold text-purple-600">
                          {Math.round((entries.filter(e => e.prompt_id).length / Math.max(entries.length, 1)) * 100)}%
                        </div>
                        <div className="text-sm text-gray-700 mt-2">📝 Guided Prompt Entries</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {entries.filter(e => e.prompt_id).length} of {entries.length} entries
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mt-4 text-center italic">
                      A balanced mix of free expression and guided reflection supports holistic growth 💫
                    </p>
                  </Card>
                </div>

                {/* Transformational Insights */}
                <Card className="p-6 bg-white/80 border-rose-200 mb-6">
                  <h3 className="font-serif font-bold text-gray-900 mb-4">Your Journey Insights 💫</h3>
                  {insights?.total_entries > 0 ? (
                    <div className="space-y-3">
                      {insights.average_mood && (
                        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                          <p className="text-sm text-gray-800 font-medium mb-2">
                            🌟 Emotional Evolution
                          </p>
                          <p className="text-sm text-gray-700">
                            {insights.average_mood >= 4.5 
                              ? `Your journal radiates positivity! This elevated emotional state (${insights.average_mood}/5) suggests you're in alignment with your authentic self. Consider: What practices or mindsets are supporting this flourishing? How can you protect and nurture this energy?`
                              : insights.average_mood >= 3.5
                              ? `You're navigating life with steady grace (${insights.average_mood}/5). This balance shows resilience. Reflection: Which small moments brought you joy this week? What boundaries are you setting to maintain your peace?`
                              : insights.average_mood >= 2.5
                              ? `Your honesty about struggle (${insights.average_mood}/5) is a sign of courage. You're in a season of transformation. Remember: The caterpillar must dissolve completely before becoming a butterfly. What support do you need right now?`
                              : `You're moving through a challenging season (${insights.average_mood}/5), and your commitment to journaling shows incredible strength. This darkness is temporary. Consider: What one small act of self-compassion can you offer yourself today?`
                            }
                          </p>
                        </div>
                      )}

                      {insights.current_streak > 0 && (
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                          <p className="text-sm text-gray-800 font-medium mb-2">
                            🔥 Consistency is Your Superpower
                          </p>
                          <p className="text-sm text-gray-700">
                            {insights.current_streak >= 7
                              ? `${insights.current_streak} days of showing up for yourself! You're not just building a habit - you're rewiring your neural pathways for self-awareness. This dedication is transforming your relationship with yourself.`
                              : insights.current_streak >= 3
                              ? `${insights.current_streak} days in a row! You're in the momentum zone. Each entry is a love letter to your future self. Keep going - transformation happens in the consistency.`
                              : `${insights.current_streak} day streak is a beautiful start! Every journey begins with a single step, and you're building the foundation for profound self-discovery.`
                            }
                          </p>
                        </div>
                      )}

                      {insights.common_themes && insights.common_themes.length > 0 && (
                        <div className="p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg">
                          <p className="text-sm text-gray-800 font-medium mb-2">
                            🌱 Patterns of Growth
                          </p>
                          <p className="text-sm text-gray-700">
                            Your soul is speaking through themes of <span className="font-semibold">{insights.common_themes.slice(0, 2).join(' and ')}</span>. 
                            {insights.common_themes.includes('gratitude') && ' Your gratitude practice is opening doors to abundance. '}
                            {insights.common_themes.includes('challenges') && ' Your willingness to face challenges shows remarkable courage. '}
                            {insights.common_themes.includes('growth') && ' Your growth mindset is your greatest asset. '}
                            {insights.common_themes.includes('self-care') && ' Prioritizing self-care is an act of revolution. '}
                            {insights.common_themes.includes('relationships') && ' Your relationships are mirrors for your own evolution. '}
                            What if these recurring themes are not problems to solve, but invitations to deeper self-understanding?
                          </p>
                        </div>
                      )}

                      {insights.entries_this_week > 0 && (
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg">
                          <p className="text-sm text-gray-800 font-medium mb-2">
                            📅 This Week's Reflection
                          </p>
                          <p className="text-sm text-gray-700">
                            {insights.entries_this_week >= 5
                              ? `${insights.entries_this_week} entries this week! You're deeply committed to your inner work. This level of self-reflection is rare and powerful. Notice how this practice is shifting your awareness in daily life.`
                              : insights.entries_this_week >= 3
                              ? `${insights.entries_this_week} entries this week shows beautiful dedication. You're creating space for your inner voice to be heard. Consider: What patterns are emerging across these reflections?`
                              : `${insights.entries_this_week} ${insights.entries_this_week === 1 ? 'entry' : 'entries'} this week. Sometimes less is more - quality over quantity. What did this week's writing reveal to you?`
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-gray-600 text-sm">Start journaling to unlock personalized transformational insights about your journey!</p>
                    </div>
                  )}
                </Card>

                {/* AI-Powered Transformational Insights */}
                {insights?.ai_insights && (
                  <div className="space-y-4 mb-6">
                    <div className="mb-3">
                      <h3 className="font-serif font-bold text-gray-900 text-lg">✨ Your Transformation Roadmap</h3>
                      <p className="text-sm text-gray-600 italic mt-1">
                        {insights.ai_insights.summary || "Personalized insights based on your journal entries"}
                      </p>
                    </div>

                    {/* 1. Micro Actions */}
                    {insights.ai_insights.micro_actions && insights.ai_insights.micro_actions.length > 0 && (
                      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                        <div className="flex items-center mb-4">
                          <Zap className="w-5 h-5 text-blue-600 mr-2" />
                          <h4 className="font-serif font-bold text-gray-900">⚡ Micro Actions</h4>
                        </div>
                        <p className="text-xs text-gray-600 italic mb-3">Small steps you can take TODAY</p>
                        <ul className="space-y-2">
                          {insights.ai_insights.micro_actions.map((action, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <input type="checkbox" className="mt-1 w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500" />
                              <span className="text-sm text-gray-700 flex-1">{action}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    )}

                    {/* 2. Wisdom Insights */}
                    {insights.ai_insights.wisdom_insights && insights.ai_insights.wisdom_insights.length > 0 && (
                      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                        <div className="flex items-center mb-4">
                          <Sparkles className="w-5 h-5 text-purple-600 mr-2" />
                          <h4 className="font-serif font-bold text-gray-900">💫 Wisdom Insights</h4>
                        </div>
                        <p className="text-xs text-gray-600 italic mb-3">Patterns and realizations from your reflections</p>
                        <ul className="space-y-3">
                          {insights.ai_insights.wisdom_insights.map((wisdom, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-purple-500 font-bold mt-0.5">{index + 1}.</span>
                              <span className="text-sm text-gray-700 flex-1 italic">{wisdom}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    )}

                    {/* 3. To-Do List */}
                    {insights.ai_insights.todo_list && insights.ai_insights.todo_list.length > 0 && (
                      <Card className="p-6 bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
                        <div className="flex items-center mb-4">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                          <h4 className="font-serif font-bold text-gray-900">✅ To-Do List</h4>
                        </div>
                        <p className="text-xs text-gray-600 italic mb-3">Concrete actions for growth this week</p>
                        <ul className="space-y-2">
                          {insights.ai_insights.todo_list.map((todo, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <input type="checkbox" className="mt-1 w-4 h-4 rounded border-green-300 text-green-600 focus:ring-green-500" />
                              <span className="text-sm text-gray-700 flex-1">{todo}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    )}

                    {/* 4. Stop Doing List */}
                    {insights.ai_insights.stop_doing_list && insights.ai_insights.stop_doing_list.length > 0 && (
                      <Card className="p-6 bg-gradient-to-br from-rose-50 to-red-50 border-rose-200">
                        <div className="flex items-center mb-4">
                          <TrendingDown className="w-5 h-5 text-rose-600 mr-2" />
                          <h4 className="font-serif font-bold text-gray-900">🛑 Stop Doing List</h4>
                        </div>
                        <p className="text-xs text-gray-600 italic mb-3">Patterns to release and let go</p>
                        <ul className="space-y-2">
                          {insights.ai_insights.stop_doing_list.map((stop, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <input type="checkbox" className="mt-1 w-4 h-4 rounded border-rose-300 text-rose-600 focus:ring-rose-500" />
                              <span className="text-sm text-gray-700 flex-1">{stop}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Journal;